import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FiArrowDown, FiDownload, FiMapPin, FiMail, FiPhone, FiGithub, FiLinkedin } from 'react-icons/fi';
// import profilePhoto from '../assets/1752741622413.jpg';
import profilePhoto from '../assets/Resume_Profile.jpg';
import resumePDF from '../assets/Kallesh_D_R.pdf';

const stats = [
  { value: '2.5+', label: 'Years Experience' },
  { value: '10+', label: 'Projects Delivered' },
  { value: '3', label: 'Live Products' },
  { value: '8.58', label: 'CGPA / 10' },
];

export default function Hero() {
  return (
    <section
      id="hero"
      className="min-h-screen bg-hero-pattern bg-grid relative overflow-hidden flex items-center"
    >
      {/* Decorative circles */}
      <div className="absolute top-24 right-10 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-float" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '4s' }} />

      <div className="section-container w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-primary-50 border border-primary-100 text-primary-700 text-sm font-semibold px-4 py-2 rounded-full"
            >
              <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
              Available for new opportunities
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-6xl xl:text-7xl text-slate-900 leading-tight">
                Kallesh
                <span className="gradient-text"> D R</span>
              </h1>
              <p className="mt-4 text-xl md:text-2xl font-display font-medium text-slate-500">
                Software Development Engineer
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-slate-600 text-base md:text-lg leading-relaxed max-w-lg"
            >
              Building scalable software products with 2+ years of expertise in full-stack development, AI/ML integration, and mobile engineering. Turning ideas into impactful technology.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex items-center gap-2 text-slate-500 text-sm"
            >
              <FiMapPin size={16} className="text-primary-500 flex-shrink-0" />
              <span>Bengaluru, Karnataka, India</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex flex-wrap gap-3"
            >
              <Link to="contact" smooth duration={600} offset={-70}>
                <button className="btn-primary cursor-pointer">
                  Get in Touch
                  <FiArrowDown size={16} />
                </button>
              </Link>
              <a
                href={resumePDF}
                download="Kallesh_D_R_Resume.pdf"
                id="hero-download-resume"
                className="btn-outline inline-flex items-center gap-2"
              >
                <FiDownload size={16} />
                Download Resume
              </a>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex gap-4"
            >
              {[
                { icon: FiGithub, href: 'https://github.com/kalleshdr', label: 'GitHub' },
                { icon: FiLinkedin, href: 'https://www.linkedin.com/in/kallesh-dr-087008235', label: 'LinkedIn' },
                { icon: FiMail, href: 'mailto:kallesh953@gmail.com', label: 'Email' },
                { icon: FiPhone, href: 'tel:+917676654809', label: 'Phone' },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-primary-600 hover:border-primary-300 hover:shadow-glow-sm transition-all duration-200"
                >
                  <Icon size={18} />
                </a>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Profile Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="flex justify-center items-center relative"
          >
            <div className="relative">
              {/* Outer ring */}
              <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-primary-200 via-accent-100 to-blue-200 opacity-60 blur-sm animate-spin-slow" />
              <div className="absolute -inset-2 rounded-full bg-gradient-to-tr from-primary-400 to-accent-400 opacity-20" />

              {/* Profile Image Container */}
              <div className="relative w-72 h-72 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-white shadow-card-hover">
                <img
                  src={profilePhoto}
                  alt="Kallesh D R — Software Development Engineer"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Floating Badges */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="floating-badge -bottom-4 -left-8 shadow-card"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <span className="text-lg">🏆</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800">Best Student</p>
                  <p className="text-xs text-slate-500">CSE 2024 Batch</p>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="floating-badge -top-4 -right-6 shadow-card"
              >
                {/* <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <span className="text-lg">🚀</span>
                </div> */}
                {/* <div>
                  <p className="text-xs font-bold text-slate-800">Live on</p>
                  <p className="text-xs text-slate-500">Google Play</p>
                </div> */}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="card p-5 text-center group hover:border-primary-200"
            >
              <p className="font-display font-bold text-3xl text-primary-600 group-hover:scale-105 transition-transform duration-200">
                {stat.value}
              </p>
              <p className="text-slate-500 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <Link to="about" smooth duration={600} offset={-70} className="cursor-pointer">
          <div className="flex flex-col items-center gap-1 text-slate-400 hover:text-primary-600 transition-colors">
            <span className="text-xs font-medium">Scroll</span>
            <FiArrowDown size={16} className="animate-bounce-slow" />
          </div>
        </Link>
      </motion.div>
    </section>
  );
}
